/* ──────────────────────────────────────────────────────────────────────
 *  ContentBookshelf — Visual bookshelf filter/navigation UI
 *
 *  Renders book spines on a shelf (desktop) or a horizontal icon strip
 *  (mobile). Each spine represents a content template; spine thickness
 *  is proportional to the number of content pieces.
 *
 *  Spec reference: docs/architecture/content-architecture-spec.md §5
 *  Data source:    src/Data/Content/contentTemplates.ts
 *
 *  Props:
 *    templates    — unlocked templates with per-template content counts
 *    lockedTemplates — templates the user hasn't unlocked yet
 *    selectedTemplate — currently active filter (null = show all)
 *    onSelect     — called when a spine is tapped (or cleared)
 *    onNewContent — called when the "+" spine is tapped
 *    debugMode    — optional debug rendering
 * ────────────────────────────────────────────────────────────────────── */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type {
  ContentTemplateKey,
  ContentTemplateDefinition,
  TrustTier,
} from '../../Data/Content/contentTemplates';
import styles from './ContentBookshelf.module.css';

/* ── Props ────────────────────────────────────────────────────────── */

export interface BookshelfTemplateItem {
  template: ContentTemplateDefinition;
  count: number;
}

interface ContentBookshelfProps {
  /** Unlocked templates with counts. Rendered in order provided. */
  templates: BookshelfTemplateItem[];
  /** Locked templates (shown as greyed silhouettes on the right). */
  lockedTemplates: ContentTemplateDefinition[];
  /** Currently selected template key (null = "All"). */
  selectedTemplate: ContentTemplateKey | null;
  /** Called when user taps a spine or clears filter. */
  onSelect: (key: ContentTemplateKey | null) => void;
  /** Called when user taps the "+" new content spine. */
  onNewContent: () => void;
  /** Optional debug rendering. */
  debugMode?: boolean;
}

/* ── Spine width calculation (§5.7) ── */

const MIN_SPINE_WIDTH = 52;
const MAX_SPINE_WIDTH = 96;

function calcSpineWidth(count: number, maxCount: number): number {
  if (maxCount <= 0) return MIN_SPINE_WIDTH;
  const ratio = count / maxCount;
  return Math.round(
    MIN_SPINE_WIDTH + ratio * (MAX_SPINE_WIDTH - MIN_SPINE_WIDTH)
  );
}

/* ── Localized trust tier label ── */

function trustTierLabelKey(tier: TrustTier): string {
  const map: Record<TrustTier, string> = {
    early_contributor: 'content:trustTier.earlyContributor',
    contributor: 'content:trustTier.contributor',
    demonstrated_quality: 'content:trustTier.demonstratedQuality',
    high_trust: 'content:trustTier.highTrust',
  };
  return map[tier];
}

/* ── Component ────────────────────────────────────────────────────── */

export default function ContentBookshelf({
  templates,
  lockedTemplates,
  selectedTemplate,
  onSelect,
  onNewContent,
  debugMode,
}: ContentBookshelfProps) {
  const { t } = useAppTranslation('content');

  /* ---- Tooltip state ---- */
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showTooltip = useCallback((key: string) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredKey(key), 300);
  }, []);

  const hideTooltip = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHoveredKey(null);
  }, []);

  /* Cleanup timer on unmount */
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  /* ---- Max count for width scaling ---- */
  const maxCount = templates.reduce(
    (max, item) => Math.max(max, item.count),
    0
  );

  /* ---- Spine click handler ---- */
  const handleSpineClick = useCallback(
    (key: ContentTemplateKey) => {
      // Tap selected spine again → clear filter
      if (selectedTemplate === key) {
        onSelect(null);
      } else {
        onSelect(key);
      }
    },
    [selectedTemplate, onSelect],
  );

  /* ---- Keyboard handler for spines ---- */
  const handleSpineKeyDown = useCallback(
    (e: React.KeyboardEvent, key: ContentTemplateKey) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSpineClick(key);
      }
    },
    [handleSpineClick],
  );

  return (
    <div className={styles.shelf} role="toolbar" aria-label={t('bookshelf.ariaLabel')}>
      {/* ── "All" button ── */}
      <button
        className={`${styles.allButton} ${selectedTemplate === null ? styles.allButtonActive : ''}`}
        onClick={() => onSelect(null)}
        type="button"
        aria-pressed={selectedTemplate === null}
      >
        {t('bookshelf.all')}
      </button>

      {/* ── Unlocked template spines ── */}
      {templates.map(({ template, count }) => {
        const isSelected = selectedTemplate === template.key;
        const spineWidth = calcSpineWidth(count, maxCount);
        const isHovered = hoveredKey === template.key;

        return (
          <div
            key={template.key}
            className={`${styles.spine} ${isSelected ? styles.spineSelected : ''}`}
            style={{
              width: `${spineWidth}px`,
              '--spine-color': template.bookshelf.spineColorToken,
            } as React.CSSProperties}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            aria-label={t(`templates.${template.key}.name`)}
            onClick={() => handleSpineClick(template.key)}
            onKeyDown={(e) => handleSpineKeyDown(e, template.key)}
            onMouseEnter={() => showTooltip(template.key)}
            onMouseLeave={hideTooltip}
            onFocus={() => showTooltip(template.key)}
            onBlur={hideTooltip}
          >
            <span className={styles.spineIcon} aria-hidden="true">
              {template.bookshelf.icon}
            </span>
            <span className={styles.spineLabel}>
              {template.bookshelf.spineLabel}
            </span>
            <span className={styles.spineCount}>{count}</span>

            {/* Tooltip */}
            {isHovered && (
              <div className={styles.tooltip}>
                <div className={styles.tooltipName}>
                  {t(`templates.${template.key}.name`)}
                </div>
                <div className={styles.tooltipMeta}>
                  {count} {count === 1 ? t('bookshelf.piece') : t('bookshelf.pieces')}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ── Locked template spines (silhouettes) ── */}
      {lockedTemplates.map((template) => {
        const isHovered = hoveredKey === template.key;

        return (
          <div
            key={template.key}
            className={`${styles.spine} ${styles.spineLocked}`}
            style={{
              width: `${MIN_SPINE_WIDTH}px`,
              '--spine-color': template.bookshelf.spineColorToken,
            } as React.CSSProperties}
            role="img"
            aria-label={t('bookshelf.lockedTemplate', {
              name: t(`templates.${template.key}.name`),
            })}
            onMouseEnter={() => showTooltip(template.key)}
            onMouseLeave={hideTooltip}
          >
            <span className={styles.spineIcon} aria-hidden="true">
              {template.bookshelf.icon}
            </span>
            <span className={styles.spineLabel}>
              {template.bookshelf.spineLabel}
            </span>
            <span className={styles.lockIcon} aria-hidden="true">
              🔒
            </span>

            {/* Locked tooltip with requirements */}
            {isHovered && (
              <div className={styles.tooltip}>
                <div className={styles.tooltipName}>
                  {t(`templates.${template.key}.name`)}
                </div>
                <div className={styles.tooltipLocked}>
                  {t(trustTierLabelKey(template.trustTier))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ── "New" / "+" spine ── */}
      <button
        className={styles.spineNew}
        onClick={onNewContent}
        type="button"
        aria-label={t('bookshelf.newContent')}
        style={{
          height: `${Math.max(
            80,
            MIN_SPINE_WIDTH + 44
          )}px`,
        }}
      >
        <span className={styles.spineNewIcon} aria-hidden="true">+</span>
        <span className={styles.spineNewLabel}>{t('bookshelf.new')}</span>
      </button>

      {/* ── Debug output ── */}
      {debugMode && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', paddingInlineStart: 12, alignSelf: 'center' }}>
          {templates.length} unlocked / {lockedTemplates.length} locked / max={maxCount}
        </div>
      )}
    </div>
  );
}
